import React, { useEffect, useState } from "react";
import { Loader2 } from "lucide-react";
import { Card } from "@/components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { EmptyState } from "@/components/PageHeader";
import api from "@/lib/api";
import { fmtMinutes } from "@/components/shownotes/meta";
import RunTimeRow, { SEGMENTS } from "@/components/shownotes/RunTimeRow";

const fmtAvg = (m) => (m == null ? "—" : fmtMinutes(Math.round(m)));

// Average Act I / Intermission / Act 2 / Total across a production's published Show Notes.
export default function RunTimesTab({ productions }) {
  const [pid, setPid] = useState(null);
  const [data, setData] = useState(null);
  useEffect(() => {
    setData(null);
    api.get("/show-notes/run-times", { params: pid ? { production_id: pid } : {} }).then(({ data }) => setData(data)).catch(() => setData({ count: 0, performances: [] }));
  }, [pid]);
  const shown = data?.production?.id || pid;

  const max = Math.max(1, ...(data?.performances || []).map((p) => p.total));
  const tiles = [...SEGMENTS, { key: "total", label: "Total Time", dot: "bg-slate-900" }];
  return (
    <div className="space-y-5">
      <div className="flex flex-wrap items-center gap-3">
        <Select value={shown || ""} onValueChange={setPid} disabled={!productions.length}>
          <SelectTrigger className="w-64 bg-white" data-testid="runtimes-production-select"><SelectValue placeholder="Choose a production" /></SelectTrigger>
          <SelectContent>{productions.map((p) => <SelectItem key={p.id} value={p.id} data-testid={`runtimes-production-${p.id}`}>{p.name}</SelectItem>)}</SelectContent>
        </Select>
        {data?.count > 0 && <span className="text-sm text-slate-500" data-testid="runtimes-count">Across {data.count} performance{data.count === 1 ? "" : "s"}</span>}
      </div>
      {!data ? (
        <div className="flex justify-center py-16 text-slate-400"><Loader2 className="h-6 w-6 animate-spin" /></div>
      ) : !data.count ? (
        <EmptyState title="No run times yet" description="Average run times appear once Show Notes with act durations are published for this production." testId="runtimes-empty" />
      ) : (
        <>
          <div className="grid grid-cols-2 gap-3 lg:grid-cols-4" data-testid="runtimes-averages">
            {tiles.map((t, i) => (
              <Card key={t.key} style={{ animationDelay: `${i * 50}ms` }} className={`border-slate-200 p-4 shadow-sm animate-fade-in-up ${t.key === "total" ? "bg-slate-900 text-white" : ""}`}>
                <p className={`flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wider ${t.key === "total" ? "text-slate-300" : "text-slate-500"}`}>
                  <span className={`h-2 w-2 rounded-full ${t.key === "total" ? "bg-white" : t.dot}`} /> Avg {t.label}
                </p>
                <p className="mt-2 font-heading text-2xl font-bold tabular-nums" data-testid={`runtimes-avg-${t.key}`}>{fmtAvg(data.averages[t.key])}</p>
              </Card>
            ))}
          </div>
          <Card className="border-slate-200 p-4 shadow-sm" data-testid="runtimes-performances">
            <p className="mb-3 text-xs font-bold uppercase tracking-wider text-slate-500">Each performance</p>
            <div className="space-y-2.5">{data.performances.map((p) => <RunTimeRow key={p.id} p={p} max={max} />)}</div>
          </Card>
        </>
      )}
    </div>
  );
}
